import { useKeycloak } from '@dsb-norge/vue-keycloak-js'
import { createRouter, createWebHistory } from 'vue-router'
import { useUserRole } from '@/stores/userRole'
import { useCustomers } from '@/stores/customers'
import { useDemoStore } from '@/stores/demoStore'
import { RES, ACT } from '@/constants/acl'
import { getField } from '@/scripts/task'
import { useMaps } from '@/stores/maps'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/',
            name: 'home',
            component: () => import('@/views/home/HomeLayout.vue'),
            meta: { isAuthenticated: true },
            children: [
                {
                    path: '',
                    name: 'home-view',
                    component: () => import('@/views/home/HomeView.vue'),
                },
                {
                    path: 'users-management',
                    name: 'users-management',
                    component: () => import('@/views/service/UsersManagementView.vue'),
                    meta: { perm: RES.USERS, act: ACT.R, demoBlocked: 'users-table' },
                },
                {
                    path: 'vehicles-management',
                    name: 'vehicles-management',
                    component: () => import('@/views/service/VehiclesManagementView.vue'),
                    meta: { perm: RES.MACHINES, act: ACT.R, demoBlocked: 'vehicles' },
                },
                {
                    path: 'fields-management',
                    name: 'fields-management',
                    component: () => import('@/views/service/FieldsManagementView.vue'),
                    meta: { demoBlocked: 'fields-table' },
                },
            ],
        },
        {
            path: '/access-denied',
            name: 'access-denied',
            component: () => import('@/views/service/AccesDenied.vue'),
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/'
        },
        {
            path: '/',
            name: 'map',
            component: () => import('@/views/GoogleMapsLayout.vue'),
            meta: { isAuthenticated: true },
            children: [
                {
                    path: 'fields/:customerId/:lat/:lng/:zoom',
                    name: 'fields-customer',
                    component: () => import('@/views/FieldsView.vue'),
                },
                {
                    path: 'fields/:lat?/:lng?/:zoom?',
                    name: 'fields',
                    component: () => import('@/views/FieldsView.vue'),
                },
                {
                    path: 'drawing/:customerId/:lat/:lng/:zoom',
                    name: 'drawing-customer',
                    component: () => import('@/components/drawing/Drawing.vue'),
                    meta: { perm: RES.FIELD_GEOM, act: ACT.C },
                },
                {
                    path: 'drawing/:lat?/:lng?/:zoom?',
                    name: 'drawing',
                    component: () => import('@/components/drawing/Drawing.vue'),
                    meta: { perm: RES.FIELD_GEOM, act: ACT.C },
                },
                {
                    path: 'fields-connection/:customerId/:lat/:lng/:zoom',
                    name: 'fields-connection-customer',
                    component: () => import('@/components/fieldConnection/FieldConnectionView.vue'),
                    meta: { perm: RES.FIELD_GEOM, act: ACT.C },
                },
                {
                    path: 'fields-connection/:lat?/:lng?/:zoom?',
                    name: 'fields-connection',
                    component: () => import('@/components/fieldConnection/FieldConnectionView.vue'),
                    meta: { perm: RES.FIELD_GEOM, act: ACT.C },
                },
                {
                    path: 'trimble-management',
                    name: 'trimble-management',
                    component: () => import('@/views/service/TrimbleManagementView.vue'),
                    meta: { perm: RES.TRIMBLE, act: ACT.R, demoBlocked: 'trimble' },
                },
                {
                    path: 'vehicle-tracking/:customerId',
                    name: 'vehicle-tracking-customer',
                    component: () => import('@/views/service/VehiclesPositionView.vue'),
                    meta: { perm: RES.MACHINES, act: ACT.R, demoBlocked: 'vehicles' },
                },
                {
                    path: 'vehicle-tracking',
                    name: 'vehicle-tracking',
                    component: () => import('@/views/service/VehiclesPositionView.vue'),
                    meta: { perm: RES.MACHINES, act: ACT.R, demoBlocked: 'vehicles' },
                },
                {
                    path: 'field/:fieldId',
                    name: 'field',
                    component: () => import('@/views/FieldLayout.vue'),
                    children: [
                        {
                            path: 'details',
                            name: 'field-details',
                            component: () => import('@/views/field/FieldDetails.vue'),
                        },
                        {
                            path: 'tasks',
                            name: 'field-tasks',
                            component: () => import('@/views/field/FieldTasks.vue'),
                        },
                        {
                            path: 'task/:taskId',
                            component: () => import('@/views/TaskLayout.vue'),
                            children: [
                                {
                                    path: '', // /field/:id/task/:taskId → /paths
                                    redirect: { name: 'task-paths' },
                                },
                                {
                                    path: 'paths',
                                    name: 'task-paths',
                                    component: () => import('@/views/TaskPathsList.vue'),
                                },
                                {
                                    path: 'path-planner',
                                    name: 'path-planner',
                                    component: () => import('@/views/RoutePlanner.vue'),
                                    meta: { perm: RES.FIELD_GEOM, act: ACT.C },
                                },
                                {
                                    path: 'path/:pathId',
                                    name: 'task-path',
                                    component: () => import('@/views/PathLayout.vue'),
                                    children: [
                                        {
                                            path: 'details',
                                            name: 'path-details',
                                            component: () => import('@/views/PathDetails.vue'),
                                        },
                                        {
                                            path: 'edit-path',
                                            name: 'edit-path',
                                            component: () => import('@/views/EditPath.vue'),
                                            meta: { perm: RES.FIELD_GEOM, act: ACT.U },
                                        },
                                        {
                                            path: 'update-path',
                                            name: 'update-path',
                                            component: () => import('@/views/UpdatePath.vue'),
                                            meta: { perm: RES.FIELD_GEOM, act: ACT.U },
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
})

router.beforeEach(async (to, _, next) => {
    const keycloak = useKeycloak()

    // wait for Keycloak to be ready
    if (!keycloak.ready) {
        await new Promise((resolve) => {
            const checkReady = () => {
                if (keycloak.ready) {
                    resolve()
                } else {
                    setTimeout(checkReady, 200)
                }
            }
            checkReady()
        })
    }

    // No organization yet → stay on home (demo setup / empty home). Allow access-denied.
    const allowWithoutOrganization =
        to.name === 'home-view' ||
        to.name === 'access-denied'

    if (!allowWithoutOrganization) {
        const cs = useCustomers()
        if (!cs.customers.length) {
            await cs.getCustomers()
        }
        if (!cs.customers.length) {
            return next({ path: '/' })
        }
    }

    // demo-blocked routes (direct URL / bookmark)
    const demoBlockedRecord = to.matched.find((r) => r.meta?.demoBlocked)
    if (demoBlockedRecord) {
        const demo = useDemoStore()
        if (demo.isBlocked(demoBlockedRecord.meta.demoBlocked)) {
            return next({ name: 'access-denied', query: { reason: 'demo' } })
        }
    }

    // permissions
    const permRecord = to.matched.find((r) => r.meta && r.meta.perm && r.meta.act)
    if (permRecord) {
        const { perm: resource, act: action } = permRecord.meta

        const ur = useUserRole()
        const cs = useCustomers()
        const map = useMaps()

        if (ur.userOrgRole == null) {
            await ur.getUserOrgRole(keycloak)
        }
        if ((!cs.customers || cs.customers.length === 0)) {
            await cs.getCustomers()
        }

        let allowed = false
        const fieldsPath = '/fields/' + map.geo.lat + '/' + map.geo.long + '/' + map.geo.zoom

        // for routes that have a fieldId in the url (example /field/:fieldId...)
        if (to.params.fieldId) {
            // set fieldId to a number
            const fieldId = Number(to.params.fieldId)
            // check if fieldId is a valid number
            if (isNaN(fieldId)) {
                console.warn('fieldId ' + to.params.fieldId + ' not found or not a number')
                return next({ path: fieldsPath })
            }

            // check if field exists
            const field = await getField(fieldId)
            if (!field) {
                console.warn('field with id ' + fieldId + ' not found in the database')
                return next({ path: fieldsPath })
            }
            // check if field is allowed
            const farmId = field.farm_id
            if (Number.isFinite(farmId)) {
                allowed = ur.can(resource, action, Number(farmId))
            }
        } else {
            // no fieldId in the url (example another route than /field/:fieldId... -> drawing, fields-connection, etc.)
            allowed = ur.hasAnyAccess(resource, action)
        }

        if (!allowed) return next({ name: 'access-denied' })
    }
    return next()
})

export default router
